/**
 * Campaign Lookups — list page.
 *
 * Lookup tables for attribute-based content substitution in email
 * templates. Each lookup has a key column, optional rule columns, and
 * one or more value columns. Templates reference lookups via
 * sourceType='lookup' field mappings.
 */

import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  PlusIcon,
  TrashIcon,
  PencilIcon,
  DocumentDuplicateIcon,
  TableCellsIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import {
  getLookups,
  deleteLookup,
  createLookup,
  getLookupRowCount,
  getLookupReferencingFlights,
} from "../services/lookups.service";
import LoadingSpinner from "../components/common/LoadingSpinner";
import type { CampaignLookup } from "../types/lookup.types";

interface ReferencingFlight {
  id: string;
  name: string;
  status: string;
  campaign_id: string | null;
}

export default function CampaignLookups() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const {
    data: lookups,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["campaign-lookups"],
    queryFn: getLookups,
  });

  // Per-lookup row counts. Cheap individual queries (idx_campaign_lookup_rows_lookup_key
  // makes the count(*) instant). Rendered next to the name.
  const rowCountQueries = useQuery({
    queryKey: ["campaign-lookup-row-counts", lookups?.map((l) => l.id)],
    queryFn: async () => {
      if (!lookups) {
        return {};
      }
      const counts: Record<string, number> = {};
      await Promise.all(
        lookups.map(async (l) => {
          counts[l.id] = await getLookupRowCount(l.id);
        }),
      );
      return counts;
    },
    enabled: !!lookups && lookups.length > 0,
  });

  const filtered = useMemo(() => {
    if (!lookups) {
      return [];
    }
    const q = search.trim().toLowerCase();
    if (!q) {
      return lookups;
    }
    return lookups.filter(
      (l) =>
        l.name.toLowerCase().includes(q) ||
        (l.description ?? "").toLowerCase().includes(q) ||
        l.key_column.toLowerCase().includes(q),
    );
  }, [lookups, search]);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteLookup(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["campaign-lookups"] });
      setDeletingId(null);
    },
    onError: (err: Error) => {
      alert(`Delete failed: ${err.message}`);
      setDeletingId(null);
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: async (source: CampaignLookup) => {
      // Duplicate the lookup metadata only — rows are not copied.
      // Marketers can re-upload or copy-paste rows in the new lookup.
      return createLookup({
        name: `${source.name} (copy)`,
        description: source.description,
        key_column: source.key_column,
        column_schema: source.column_schema,
      });
    },
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ["campaign-lookups"] });
      navigate(`/campaigns/lookups/${created.id}`);
    },
    onError: (err: Error) => alert(`Duplicate failed: ${err.message}`),
  });

  if (isLoading) {
    return (
      <div className="p-8">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4 text-red-800">
          Failed to load lookups: {(error as Error).message}
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Campaign Lookups</h1>
          <p className="text-sm text-gray-600 mt-1">
            Attribute-based content substitution for email and SMS templates.
            Reference these from a flight's variable mapper to switch values per
            contact (e.g. state-specific links).
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate("/campaigns/lookups/new")}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          <PlusIcon className="h-5 w-5" />
          New Lookup
        </button>
      </div>

      <div className="mb-4 max-w-md relative">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
        <input
          type="text"
          placeholder="Search lookups…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {filtered.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 rounded-md p-12 text-center">
          <TableCellsIcon className="mx-auto h-12 w-12 text-gray-300" />
          <p className="mt-2 text-sm text-gray-600">
            {search
              ? "No lookups match your search."
              : "No lookups yet. Create one to start using attribute-based content in your campaigns."}
          </p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Key Column</th>
                <th className="px-4 py-3 text-right">Rows</th>
                <th className="px-4 py-3">Last Updated</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((lookup) => {
                const rowCount = rowCountQueries.data?.[lookup.id];
                return (
                  <tr key={lookup.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        className="text-left font-medium text-blue-700 hover:underline"
                        onClick={() =>
                          navigate(`/campaigns/lookups/${lookup.id}`)
                        }
                      >
                        {lookup.name}
                      </button>
                      {lookup.description && (
                        <div className="text-xs text-gray-500 mt-0.5">
                          {lookup.description}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-700">
                      {lookup.key_column}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-gray-700">
                      {rowCount === undefined ? "…" : rowCount}
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">
                      {new Date(lookup.updated_at).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          type="button"
                          title="Edit"
                          onClick={() =>
                            navigate(`/campaigns/lookups/${lookup.id}`)
                          }
                          className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          title="Duplicate"
                          onClick={() => duplicateMutation.mutate(lookup)}
                          disabled={duplicateMutation.isPending}
                          className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded disabled:opacity-50"
                        >
                          <DocumentDuplicateIcon className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          title="Delete"
                          onClick={() => setDeletingId(lookup.id)}
                          className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/*
        Delete confirmation modal. The hard-block-when-in-use behavior
        (#70) replaces this with a referencing-flights modal.
      */}
      {deletingId && (
        <DeleteLookupModal
          lookupId={deletingId}
          lookupName={lookups?.find((l) => l.id === deletingId)?.name ?? ""}
          onCancel={() => setDeletingId(null)}
          onConfirm={() => deleteMutation.mutate(deletingId)}
          isDeleting={deleteMutation.isPending}
        />
      )}
    </div>
  );
}

// ============================================================================
// Delete-with-referenced-by-check modal (pulls #70 forward)
// ============================================================================
//
// Per the audit-followup #2 to #68: a manager/admin shouldn't be able
// to delete a lookup that's actively being used by a scheduled or
// in-flight send, because the resolver would silently fall through to
// fallback at send time.
//
//   - scheduled / sending  → hard-block. Show the flights with click-
//                            through, send button disabled.
//   - draft                → warn-but-allow. Marketer must confirm
//                            they understand drafts will need their
//                            mappings fixed.
//   - sent / cancelled / paused → allow. History is already written;
//                                  deleting the lookup doesn't affect
//                                  past sends.

const HARD_BLOCK_STATUSES = new Set(["scheduled", "sending"]);
const WARN_BUT_ALLOW_STATUSES = new Set(["draft"]);

function DeleteLookupModal({
  lookupId,
  lookupName,
  onCancel,
  onConfirm,
  isDeleting,
}: {
  lookupId: string;
  lookupName: string;
  onCancel: () => void;
  onConfirm: () => void;
  isDeleting: boolean;
}) {
  const navigate = useNavigate();
  const flightsQ = useQuery({
    queryKey: ["lookup-referencing-flights", lookupId],
    queryFn: () => getLookupReferencingFlights(lookupId),
  });

  const flights = (flightsQ.data ?? []) as ReferencingFlight[];
  const blocking = flights.filter((f) => HARD_BLOCK_STATUSES.has(f.status));
  const warningDrafts = flights.filter((f) =>
    WARN_BUT_ALLOW_STATUSES.has(f.status),
  );
  const isHardBlocked = blocking.length > 0;
  const needsWarning = warningDrafts.length > 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[80vh] overflow-y-auto">
        <div className="border-b border-gray-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">
            {isHardBlocked
              ? "Cannot delete this lookup"
              : "Delete this lookup?"}
          </h2>
          <p className="text-sm text-gray-600 mt-1 font-mono">{lookupName}</p>
        </div>

        <div className="p-6 space-y-4">
          {flightsQ.isLoading ? (
            <div className="text-sm text-gray-500">
              Checking which flights reference this lookup…
            </div>
          ) : (
            <>
              {isHardBlocked && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3">
                  <p className="text-sm font-medium text-red-900">
                    This lookup is referenced by {blocking.length} active flight
                    {blocking.length === 1 ? "" : "s"}. Cancel or reschedule{" "}
                    {blocking.length === 1 ? "it" : "them"} before deleting:
                  </p>
                  <ul className="mt-2 space-y-1">
                    {blocking.map((f) => (
                      <li key={f.id} className="text-sm">
                        <button
                          type="button"
                          className="text-red-700 hover:underline"
                          onClick={() =>
                            navigate(
                              f.campaign_id
                                ? `/campaigns/${f.campaign_id}/flights/${f.id}`
                                : `/campaigns`,
                            )
                          }
                        >
                          {f.name}
                        </button>{" "}
                        <span className="text-xs text-red-600 uppercase">
                          ({f.status})
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {!isHardBlocked && needsWarning && (
                <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                  <p className="text-sm font-medium text-amber-900">
                    {warningDrafts.length} draft flight
                    {warningDrafts.length === 1 ? "" : "s"} reference
                    {warningDrafts.length === 1 ? "s" : ""} this lookup. Their
                    lookup mappings will need to be fixed before they can be
                    sent.
                  </p>
                  <ul className="mt-2 space-y-1">
                    {warningDrafts.map((f) => (
                      <li key={f.id} className="text-sm text-amber-800">
                        {f.name}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {!isHardBlocked && !needsWarning && (
                <p className="text-sm text-gray-700">
                  This will remove the lookup and all its rows. No active
                  flights reference it. This cannot be undone.
                </p>
              )}
            </>
          )}
        </div>

        <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          {!isHardBlocked && (
            <button
              type="button"
              onClick={onConfirm}
              disabled={isDeleting || flightsQ.isLoading}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
            >
              {isDeleting ? "Deleting…" : "Delete"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
